import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { EMPTY, merge, of, from, zip } from 'rxjs';
import { mergeMap, take, map, catchError, switchMap, filter } from 'rxjs/operators';

import { GuidService } from 'ui-components/services/guid-service';
import { ModalService } from 'ui-components/services/modal-service';

import * as actions from './demos.actions';
import * as DemosApi from './demos.api';
import { GenericErrorRetryContainer } from '../../containers/generic-error-retry-container';
import { ORDER_DEMO_TYPE, DEMO_STORE } from '../../demos/demo-orders';
import { DEFAULT_ORDERS_PAGE_URL } from '../../services/user-permission-service';
import Global from '../global-selectors';
import { messages } from '../orders/messages';
import { goToOrderDetails } from '../orders/order-details/orders.details.actions';
import { GET_ORDERS_SUCCESS, requestGetOrders } from '../orders/orders.actions';
import { sanitizeOrder } from '../orders/orders.epics';
import { updateSellerShipFroms, refreshSellerDataByProperty } from '../seller/seller.actions';
import { getShipFroms, getStores } from '../seller/seller.api';
import { getSellerTagsSuccess } from '../tags/tags.actions';
import { getSellerTags } from '../tags/tags.api';

const DEFAULT_DEMO_STORE_GUID = '0';

const getDemoStoreGuid = (getState) => {
  const sellerStores = Global.selectSellerStores(getState());
  const activeDemoStore = sellerStores.find((ds) => ds.name === DEMO_STORE && ds.isActive);
  const demoStoreGuid = activeDemoStore?.storeGuid ?? DEFAULT_DEMO_STORE_GUID;
  return demoStoreGuid;
};

export const orderToursEpic = (action$, { getState, state$ }, { push }) => {
  const handler = 'createAnother';
  return action$.pipe(
    ofType(actions.DEMO_SETUP_START),
    switchMap((action) => {
      if (!action.demoTypes.includes(ORDER_DEMO_TYPE)) {
        return EMPTY;
      }

      if (!action.order) {
        return EMPTY;
      }
      const state = getState();
      const sellerTimeZone = Global.selectSellerTimeZoneIANA(state);
      const userDateFormat = Global.selectUserUserDateFormat(state);
      // currently we are only creating single orders but that will most likely change in the future
      const orderCopy = JSON.parse(JSON.stringify(action.order));
      const sanitizedOrder = sanitizeOrder(orderCopy, userDateFormat, sellerTimeZone);
      const workflowGuid = GuidService.newGuid();
      if (action.shipFrom !== undefined && action.shipFrom !== null) {
        sanitizedOrder.shipFrom = JSON.parse(JSON.stringify(action.shipFrom));
      }
      const productsCopy = JSON.parse(JSON.stringify(action.demoProducts));
      sanitizedOrder.createGuid = GuidService.newGuid();
      sanitizedOrder.orderDateTime = moment();
      sanitizedOrder.paidDateTime = moment();
      sanitizedOrder.demoProducts = productsCopy;
      sanitizedOrder.demoTag = action.tagData;
      let salesOrderId = 0;
      return merge(
        from(DemosApi.createDemoOrder(sanitizedOrder)).pipe(
          map(({ data }) => {
            salesOrderId = data.salesOrderId;

            return actions.createDemoOrderSuccess(data, handler, workflowGuid);
          }),
          catchError(({ response, message }) => {
            ModalService.launchModal({
              component: GenericErrorRetryContainer,
              props: {
                action,
                title: <FormattedMessage {...messages.createOrderErrorTitle} />,
                message: <FormattedMessage {...messages.createOrderErrorMessage} />,
              },
            });

            return of(actions.createDemoOrderFailure(response, message, workflowGuid));
          }),
        ),
        action$.pipe(
          ofType(actions.CREATE_DEMO_ORDER_SUCCESS),
          take(1),
          mergeMap(() => {
            return zip(
              getShipFroms().pipe(
                map((resp) => updateSellerShipFroms(resp)),
                catchError(() => EMPTY),
              ),
              getSellerTags().pipe(
                map((resp) => getSellerTagsSuccess(resp)),
                catchError(() => EMPTY),
              ),
              getStores().pipe(
                map((resp) => refreshSellerDataByProperty('stores', resp)),
                catchError(() => EMPTY),
              ),
            ).pipe(
              mergeMap(([shipFromsAction, tagsAction, storeActions]) => {
                return merge(of(shipFromsAction), of(tagsAction), of(storeActions), of(requestGetOrders()));
              }),
            );
          }),
        ),
        action$.pipe(
          ofType(GET_ORDERS_SUCCESS),
          take(1),
          mergeMap(() => {
            const demoStoreGuid = getDemoStoreGuid(getState);
            push(`${DEFAULT_ORDERS_PAGE_URL}/store/${demoStoreGuid}`);

            return state$.pipe(
              filter((_state) => {
                const fpIds = Global.selectEntitiesFulfillmentPlanIdsBySalesOrderIds(_state, { salesOrderIds: [salesOrderId] });
                return !!fpIds.length;
              }),
              take(1),
              mergeMap(() => {
                const demoIds = Global.selectEntitiesFulfillmentPlanIdsBySalesOrderIds(getState(), {
                  salesOrderIds: [salesOrderId],
                });
                if (demoIds.length > 0) {
                  return merge(
                    of(actions.setExistingDemoOrder(parseInt(demoIds[0], 10))),
                    of(
                      goToOrderDetails({
                        salesOrderId,
                        fulfillmentPlanId: demoIds[0].toString(),
                        replaceRoute: true,
                      }),
                    ),
                  );
                }
                return EMPTY;
              }),
            );
          }),
        ),
      );
    }),
  );
};

export const openExistingDemoOrderEpic = (action$, { getState }, { push }) => {
  return action$.pipe(
    ofType(actions.OPEN_EXISTING_DEMO_ORDER),
    switchMap(() => {
      const demoStoreGuid = getDemoStoreGuid(getState);
      push(`${DEFAULT_ORDERS_PAGE_URL}/store/${demoStoreGuid}`);
      return action$.pipe(
        ofType(GET_ORDERS_SUCCESS),
        take(1),
        map(() => {
          const demoOrder = Global.selectSellerDemoFulfillmentPlanId(getState());
          const [salesOrderId] = Global.selectEntitiesSalesOrderIdsByFulfillmentPlanIds(getState(), {
            fulfillmentPlanIds: [demoOrder],
          });
          return goToOrderDetails({
            salesOrderId,
            fulfillmentPlanId: demoOrder.toString(),
          });
        }),
      );
    }),
  );
};

export const deactivateDemoOrderEpic = (action$, { getState }, { push }) => {
  return action$.pipe(
    ofType(actions.DEACTIVATE_DEMO_ORDER),
    switchMap((action) => {
      const { demoFulfillmentPlanId } = action;
      const shipFrom = action.demoShipFrom;
      const { demoTag } = action;
      const { demoProducts } = action;
      const payload = {
        demoFulfillmentPlanId,
        shipFrom,
        demoTag,
        demoProducts,
      };
      const demoStoreGuid = getDemoStoreGuid(getState);
      return merge(
        from(DemosApi.deactivateDemoOrder(demoStoreGuid, payload)).pipe(
          map(() => actions.deactivateDemoOrderSuccess()),
          catchError(() => {
            ModalService.launchModal({
              component: GenericErrorRetryContainer,
              props: {
                action: actions.deactivateDemoOrder(),
                title: <FormattedMessage {...messages.deactivateDemoOrderErrorTitle} />,
                message: <FormattedMessage {...messages.deactivateDemoOrderErrorMessage} />,
              },
            });
            return of(actions.deactivateDemoOrderFailure());
          }),
        ),
        action$.pipe(
          ofType(actions.DEACTIVATE_DEMO_ORDER_SUCCESS),
          take(1),
          mergeMap(() => {
            push(`${DEFAULT_ORDERS_PAGE_URL}/store`);

            return zip(
              getShipFroms().pipe(
                map((resp) => updateSellerShipFroms(resp)),
                catchError(() => EMPTY),
              ),
              getSellerTags().pipe(
                map((resp) => getSellerTagsSuccess(resp)),
                catchError(() => EMPTY),
              ),
              getStores().pipe(
                map((resp) => refreshSellerDataByProperty('stores', resp)),
                catchError(() => EMPTY),
              ),
            ).pipe(
              mergeMap(([shipFromsAction, tagsAction, storeActions]) => {
                return merge(
                  of(shipFromsAction),
                  of(tagsAction),
                  of(storeActions),
                  of(actions.setExistingDemoOrder(0)),
                  of(requestGetOrders()),
                );
              }),
            );
          }),
        ),
      );
    }),
  );
};
